import { _, helpers, React, ts, ui, useSelector } from '_core';

import { transform } from './helpers';
import { Params } from './types';

type ChartProps = {
  id: string;
  readOnly: boolean;
  widgetKey: string;
  portfolios: string;
  fullScreen: boolean;
  hasBenchmark: boolean;
  params: Params;
  loadWidgetData: (_payload: ts.types.widgets.WidgetGetDataParams) => Promise<ts.types.widgets.WidgetGetDataResponse>;
  title: string;
};

const Chart = ({
  id,
  readOnly,
  loadWidgetData,
  portfolios,
  fullScreen,
  params,
  hasBenchmark = false,
  title,
}: ChartProps) => {
  const [data, setData] = React.useState(null);
  const [error, setError] = React.useState(null);

  const timeSeries = useSelector((state) => state.resources.time_series);
  const selectedTimeSeries = React.useMemo(() => {
    return params.analysis_time_series_ids.map((tid) => timeSeries.find((ts) => ts.id == tid));
  }, []);

  const loadData = async () => {
    try {
      const response = await loadWidgetData({
        data: {
          file: portfolios,
        },
      });
      if (response) {
        const resp = response.data.map((el) => ({
          date: el.end_date,
          ..._.omit(el, ['start_date', 'end_date']),
        }));
        setData(resp);
      }
    } catch (err) {
      setError(JSON.stringify(err));
    }
  };
  React.useEffect(() => {
    if (portfolios) loadData();
    return () => setData(null);
  }, [portfolios]);

  const formattedData = React.useMemo(() => {
    if (!data) return null;
    return transform(data, params.isBasket, hasBenchmark, selectedTimeSeries, params.showTargetWeight);
  }, [data, params.isBasket, hasBenchmark, selectedTimeSeries, params.showTargetWeight]);

  const inSameScale = React.useMemo(() => {
    if (!formattedData) return [];
    return _.without(
      Object.keys(_.omit(formattedData?.[0], 'date')),
      ...selectedTimeSeries.map(helpers.resourcesUtils.getResourceLabel)
    );
  }, [formattedData, selectedTimeSeries]);

  return (
    <ui.MultiScaleLineChart
      id={id}
      data={formattedData}
      fullScreen={fullScreen}
      readOnly={readOnly}
      alert={error ? { type: 'error', message: helpers.parseApiError(error) } : undefined}
      exportTitle={title}
      inSameScale={inSameScale}
    />
  );
};

export default Chart;
